'use client';

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ThemedImage } from '@/components/ui/themed-image';
import {
  Check,
  Sparkle,
  Spinner,
} from '@phosphor-icons/react';
import { useTheme } from 'next-themes';
import { cn } from '@/lib/utils';
import { useCheckoutStore, selectBonusState } from '@/stores/checkout-store';
import { useAuthStore, selectIsAuthenticated } from '@/stores/auth-store';
import { useCartStore, selectCartItems, selectFirstOrderEligiblePrice } from '@/stores/cart-store';
import { useMoneyRules } from '@/lib/money-rules';
import { apiClient } from '@/lib/api';

// ========== CONSTANTS ==========
//
// Порог суммы и id подарочного ролла здесь БОЛЬШЕ НЕ ЗАШИТЫ. Оба значения
// приходят с сервера (GET /api/money-rules) и правятся в админке: раньше 1200 ₽
// было записано на сайте и в приложении отдельно от бэкенда, а id 77 — ещё в
// трёх местах. Экран показывает ровно тот порог, по которому сервер потом
// примет решение.

// ========== TYPES ==========

interface BonusRollProduct {
  id: number;
  name: string;
  price: number;
  image_url: string;
  sku?: string;
  description?: string;
}

interface BonusCheckStepProps {
  onNext?: () => void;
  onBack?: () => void;
}

// ========== HELPER FUNCTIONS ==========

const formatPrice = (price: number): string => {
  return Math.round(price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' ₽';
};

// ========== CONDITION ITEM COMPONENT ==========

function ConditionItem({ 
  text, 
  isCompleted, 
  isLoading,
  isDark 
}: { 
  text: string; 
  isCompleted: boolean;
  isLoading?: boolean;
  isDark: boolean;
}) {
  return (
    <div className="flex items-start gap-3 mb-3">
      <div className={cn(
        'w-6 h-6 rounded-pill flex items-center justify-center flex-shrink-0 mt-0.5 transition-all',
        isLoading 
          ? 'bg-[var(--surface-tertiary)] border border-[var(--border-default)]'
          : isCompleted 
            ? 'bg-[var(--primary)]/10 border border-[var(--primary)]/30' 
            : 'bg-[var(--surface-tertiary)] border border-[var(--border-default)]'
      )}>
        {isLoading ? (
          <Spinner weight="bold" className="w-3 h-3 text-[var(--primary)] animate-spin" />
        ) : isCompleted ? (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <Check weight="bold" className="w-3 h-3 text-[var(--primary)]" />
          </motion.div>
        ) : null}
      </div>
      <p className={cn(
        'text-sm leading-relaxed',
        'text-[var(--text-primary)]',
        isCompleted ? 'opacity-100' : 'opacity-60'
      )}>
        {text}
      </p>
    </div>
  );
}

// ========== STATUS BADGE COMPONENT ==========

function StatusBadge({
  status
}: {
  status: 'checking' | 'available' | 'unavailable';
}) {
  const config = {
    checking: { color: 'bg-[var(--warning)]', text: 'Проверяется...' },
    available: { color: 'bg-[var(--success)]', text: 'Доступен' },
    unavailable: { color: 'bg-[var(--text-disabled)]', text: 'Недоступен' },
  };
  
  const { color, text } = config[status];
  
  return (
    <div className="flex items-center gap-2">
      <div className={cn('w-2 h-2 rounded-pill', color)} />
      <span className="text-xs text-white/90 font-medium">{text}</span>
    </div>
  );
}

// ========== MAIN COMPONENT ==========

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function BonusCheckStep(_props: BonusCheckStepProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  
  // Bonus roll product state
  const [bonusRoll, setBonusRoll] = useState<BonusRollProduct | null>(null);
  const [isLoadingProduct, setIsLoadingProduct] = useState(true);
  
  // Auth Store
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const openAuthModal = useAuthStore((state) => state.openAuthModal);
  
  // Cart Store
  // Для ролла за регистрацию сет дня НЕ суммируется (отдельный селектор).
  const promoEligiblePrice = useCartStore(selectFirstOrderEligiblePrice);
  const addBonusItem = useCartStore((state) => state.addBonusItem);
  const cartItems = useCartStore(selectCartItems);
  const hasBonusInCart = cartItems.some((item) => item.bonusType === 'first_order');

  // Денежные правила с сервера: порог подарка и сам подарочный товар
  const moneyRules = useMoneyRules();
  const firstOrderThreshold = moneyRules.first_order.min_sum;
  const bonusRollId = moneyRules.first_order.product_id;
  
  // Checkout Store
  const bonusState = useCheckoutStore(selectBonusState);
  const setBonusState = useCheckoutStore((state) => state.setBonusState);
  
  // Load bonus roll product from API
  useEffect(() => {
    const loadBonusRoll = async () => {
      try {
        const product = await apiClient.get<BonusRollProduct>(`/products/${bonusRollId}?include_hidden=true`);
        setBonusRoll(product);
      } catch (error) {
        console.error('Failed to load bonus roll:', error);
        // Fallback to default values
        setBonusRoll({
          id: bonusRollId,
          name: 'Ролл за первый заказ',
          price: 0,
          image_url: '/placeholder.png',
        });
      } finally {
        setIsLoadingProduct(false);
      }
    };

    loadBonusRoll();
  }, [bonusRollId]);

  // Computed values
  const isEligibleBySum = promoEligiblePrice >= firstOrderThreshold;
  const remainingSum = Math.max(0, firstOrderThreshold - promoEligiblePrice);
  const progress = Math.min(100, (promoEligiblePrice / firstOrderThreshold) * 100);
  
  const isAccountCreated = isAuthenticated;
  const isSumReached = isEligibleBySum;
  const isFirstOrderValid = bonusState.isFirstOrderChecked && bonusState.isFirstOrder;
  const isGiftAvailable = isAccountCreated && isSumReached && isFirstOrderValid;

  // Статуса «Использован» здесь больше нет: тому, кто подарок уже получил,
  // шаг не показывается вовсе (useVisibleCheckoutSteps). Красный крестик
  // «Вы уже получили подарок за первый заказ» посреди оплаты — последнее, что
  // должен видеть постоянный клиент перед кнопкой «Оплатить».
  const getStatus = (): 'checking' | 'available' | 'unavailable' => {
    if (bonusState.isCheckingFirstOrder) return 'checking';
    if (isGiftAvailable) return 'available';
    return 'unavailable';
  };
  
  // Проверка «первый ли это заказ» переехала в useVisibleCheckoutSteps: ответ
  // нужен ДО показа шага, иначе шаг не пропустить (вопрос 19). Вместе с ней
  // ушла искусственная пауза setTimeout(1500) — она задерживала показ экрана
  // и ничего не улучшала. Здесь остаётся только выдача подарка.
  //
  // Условие проверяется по СОСТАВУ КОРЗИНЫ, а не по флагу «уже добавляли»:
  // подписка в cart-store снимает ролл, когда сумма падает ниже порога, и по
  // флагу он бы обратно не вернулся, хотя условие снова выполняется.
  useEffect(() => {
    if (!isGiftAvailable || hasBonusInCart || !bonusRoll || isLoadingProduct) return;

    addBonusItem({
      id: bonusRoll.id,
      name: bonusRoll.name,
      price: 0,
      image_url: bonusRoll.image_url,
      is_available: true,
      status: 'available',
      spiciness: 'none',
      sku: bonusRoll.sku || '',
      description: 'Бонусный ролл за первый заказ',
      categories: [],
    }, 'first_order');
    setBonusState({ bonusRollAdded: true });
  }, [isGiftAvailable, hasBonusInCart, bonusRoll, isLoadingProduct, addBonusItem, setBonusState]);


  // Handle auth click
  const handleAuthClick = () => {
    openAuthModal('login');
  };
  
  return (
    <div className="p-4">
      {/* Main Card */}
      {/* shadow-[var(--primary)]/20 отсюда убран: он НИКОГДА не красил тень
          фиолетовым. Проверено сборкой — класс пишет --tw-shadow, а не цвет,
          и shadow-xl объявлен в CSS позже, поэтому тень рисовалась чёрной.
          Чинить «по-настоящему» отклонено владельцем (ответ 35, вариант В). */}
      <div className={cn(
        'relative overflow-hidden rounded-card',
        'bg-[var(--primary)]',
        'shadow-float'
      )}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-pill blur-3xl transform translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-[var(--fuchsia-200)] rounded-pill blur-3xl transform -translate-x-1/2 translate-y-1/2" />
        </div>
        
        <div className="relative p-5">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">
              Ролл за первый заказ
            </h3>
            <StatusBadge status={getStatus()} />
          </div>
          
          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1">
                {isEligibleBySum && <Sparkle weight="fill" className="w-4 h-4 text-white" />}
                <span className="text-sm text-white/90 font-medium">
                  {isEligibleBySum ? 'Готово!' : 'До подарка'}
                </span>
              </div>
              <span className="text-sm text-white/90 font-semibold">
                {Math.round(progress)}%
              </span>
            </div>
            
            <div className="h-2 rounded-hairline bg-white/20 overflow-hidden">
              <motion.div
                className="h-full bg-white rounded-hairline"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              />
            </div>
            
            {remainingSum > 0 && (
              <p className="text-sm text-white/80 mt-2">
                Добавьте товаров на {formatPrice(remainingSum)}
              </p>
            )}
          </div>
          
          {/* Product Card */}
          <div className={cn(
            'rounded-card p-3 mb-4',
            isDark ? 'bg-[var(--surface)]' : 'bg-white'
          )}>
            <div className="flex items-center gap-3">
              <div className="relative w-16 h-16 rounded-card overflow-hidden bg-[var(--surface-secondary)]">
                {isLoadingProduct ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <Spinner weight="bold" className="w-6 h-6 text-[var(--primary)] animate-spin" />
                  </div>
                ) : (
                  <ThemedImage
                    src={bonusRoll?.image_url}
                    alt={bonusRoll?.name || 'Бонусный ролл'}
                    fill
                    className="object-cover"
                    sizes="64px"
                  />
                )}
              </div>
              
              <div className="flex-1">
                <h4 className={cn(
                  'font-semibold text-sm',
                  'text-[var(--text-primary)]'
                )}>
                  {bonusRoll?.name || 'Бонусный ролл'}
                </h4>
                <p className="text-sm font-medium text-[var(--pink-500)]">
                  Подарок
                </p>
              </div>
              
              <div className={cn(
                'px-3 py-1.5 rounded-pill text-xs font-medium',
                'bg-[var(--primary-subtle)] text-[var(--primary)]'
              )}>
                Доступен 1 раз
              </div>
            </div>
          </div>
          
          {/* Conditions Card */}
          <div className={cn(
            'rounded-card p-4',
            isDark ? 'bg-[var(--surface)]' : 'bg-white'
          )}>
            <h4 className={cn(
              'font-semibold text-sm mb-3',
              'text-[var(--text-primary)]'
            )}>
              Условия получения подарка
            </h4>
            
            <ConditionItem
              text="Создайте аккаунт, указав актуальный номер телефона"
              isCompleted={isAccountCreated}
              isDark={isDark}
            />
            
            <ConditionItem
              text={`Сделайте заказ на сумму от ${formatPrice(firstOrderThreshold)} (без учета доставки)`}
              isCompleted={isSumReached}
              isDark={isDark}
            />
            
            <ConditionItem
              text="Бесплатный ролл будет автоматически добавлен к заказу"
              isCompleted={isFirstOrderValid && hasBonusInCart}
              isLoading={bonusState.isCheckingFirstOrder}
              isDark={isDark}
            />
          </div>
          
          {/* Auth Button (if not authenticated) */}
          {!isAuthenticated && (
            <motion.button
              onClick={handleAuthClick}
              className={cn(
                'w-full mt-4 py-3.5 rounded-card font-semibold text-sm',
                'bg-[var(--pink-500)] text-white',
                // shadow-[var(--pink-500)]/25 удалён — та же мёртвая связка,
                // что и на карточке выше: тень была чёрной, а не розовой.
                'shadow-float',
                'hover:bg-[var(--pink-500)]/90 transition-colors'
              )}
              whileTap={{ scale: 0.98 }}
            >
              Войти и получить подарок
            </motion.button>
          )}
          
          {/* Success Message */}
          {isGiftAvailable && hasBonusInCart && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={cn(
                'mt-4 p-3 rounded-card border',
                'bg-[var(--success)]/10 border-[var(--border-success)]'
              )}
            >
              <div className="flex items-center gap-2">
                <Check weight="bold" className="w-4 h-4 text-[var(--success)]" />
                <p className="text-sm text-[var(--success)] font-medium">
                  Подарок добавлен в корзину!
                </p>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

